import { useEffect, useState } from "react";
import { Button, Checkbox, Form, Table } from "antd";
import { RegionTypes } from "../../../types/types";
import RegionTag from "../../../components/tags/RegionTag";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { useGetUserProgramsByServiceLazyQuery } from "../../../graphql/operations/get-user-program-by-service";
import { useDeleteUserProgramMutation } from "../../../graphql/operations/delete-user-program";
import { useCreateUserProgramMutation } from "../../../graphql/operations/create-user-program";

interface UserListProps {
  users: DataType[];
}

interface DataType {
  id: string;
  name: string;
  market: RegionTypes[];
  videoManagement: boolean;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  record: DataType;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  record,
  index,
  children,
  ...restProps
}) => {
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const DemoAppUserList = ({ users }: UserListProps) => {
  const [form] = Form.useForm();
  const [data, setData] = useState(users);
  const [editingKey, setEditingKey] = useState<string | undefined>("");
  const [getUserPrograms] = useGetUserProgramsByServiceLazyQuery();
  const [deleteUserProgram] = useDeleteUserProgramMutation();
  const [createUserProgram] = useCreateUserProgramMutation();

  const isEditing = (record: DataType) => record.id === editingKey;

  const edit = (record: Partial<DataType>) => {
    form.setFieldsValue({
      releaseVersion: record.videoManagement,
      ...record,
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await form.validateFields()) as DataType;
      const newData = [...data];
      const index = newData.findIndex((item) => key === item.id);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setData(newData);
        setEditingKey("");
        getUserPrograms({
          variables: {
            userId: parseInt(renderDecodedId(newData[index].id)),
            serviceId: 9,
          },
        })
          .then((r) => {
            if (
              r.data?.wf_adminFirst_afUserProgram?.edges &&
              r.data?.wf_adminFirst_afUserProgram?.edges.length > 0
            ) {
              const deleteData = r.data.wf_adminFirst_afUserProgram.edges.map(
                (program) => ({
                  afUserId: program.node.afUserId,
                  afUserProgramId: parseInt(renderDecodedId(program.node.id)),
                }),
              );
              console.log(deleteData);
              deleteUserProgram({ variables: { deleteData } }).catch((e) =>
                console.log(e),
              );
            }
          })
          .then(() => {
            const createData: any = [];
            if (newData[index].videoManagement) {
              createData.push({
                afUserId: parseInt(renderDecodedId(newData[index].id)),
                afProgramId: 26,
              });
            }
            console.log(createData);
            createUserProgram({ variables: { createData } }).catch((e) =>
              console.log(e),
            );
          })
          .catch((e) => console.log(e));
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns: any = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 200,
    },
    {
      title: "Market",
      dataIndex: "market",
      key: "market",
      render: (value: RegionTypes[]) => (
        <div style={{ display: "flex" }}>
          {value.map((region) => (
            <div key={region} style={{ marginRight: 8 }}>
              <RegionTag key={region} region={region} />
            </div>
          ))}
        </div>
      ),
      width: 200,
    },
    {
      title: "Video Management",
      dataIndex: "videoManagement",
      key: "videoManagement",
      render: (value: boolean) => <Checkbox disabled defaultChecked={value} />,
      width: 120,
      editable: true,
    },
    {
      title: "Actions",
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);
        return editable ? (
          <div style={{ display: "flex" }}>
            <Button
              type="link"
              style={{ padding: 0, marginRight: 8 }}
              onClick={() => save(record.id)}
            >
              Save
            </Button>
            <Button type="link" style={{ padding: 0 }} onClick={cancel}>
              Cancel
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex" }}>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => edit(record)}
            >
              Edit
            </Button>
          </div>
        );
      },
      fixed: "right",
      width: 120,
    },
  ];

  const mergedColumns = columns.map((col: any) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  useEffect(() => {
    if (users) {
      setData(users);
    }
  }, [users]);

  return (
    <div className="user-list-table-container">
      <Form form={form} component={false}>
        <Table
          rowKey={(record: DataType) => record.id}
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          columns={mergedColumns}
          dataSource={data}
          rowClassName="editable-row"
          pagination={false}
          scroll={{
            x: "calc(100% - 96px - 200px - 120px)",
            y: window.innerHeight * 0.7 - 48 - 55,
          }}
        />
      </Form>
    </div>
  );
};

export default DemoAppUserList;
